import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Calendar, Users, Clock, Utensils } from "lucide-react";

const LadiesClinics = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const schedule = [
    { date: "May 13th", label: "Clinic" },
    { date: "May 20th", label: "Clinic" },
    { date: "May 27th", label: "Clinic" },
    { date: "June 3rd", label: "Clinic + Dinner & Drink", highlight: true },
    { date: "June 10th", label: "Rain Date" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[450px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/6256854/pexels-photo-6256854.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">For Women</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">2026 Ladies Clinics</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            A welcoming, supportive environment for women to learn, improve, and enjoy the game of golf.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            {/* Left: Details */}
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Beginner Program</span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">2026 Ladies Beginner Clinic</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                4 clinics with a <strong>dinner and drink included</strong> on the final night! Clinics run on <strong>Wednesday evenings from 5:30 to 6:30 PM</strong>.
              </p>

              {/* Cost */}
              <div className="flex items-center gap-4 mb-8">
                <div className="bg-[#1B2A4A] text-white px-6 py-3 rounded-lg font-bold text-xl">$150</div>
                <span className="text-gray-400 text-sm">tax included</span>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Users, label: "Beginner Friendly", desc: "No experience required" },
                  { icon: Clock, label: "Wed. Evenings", desc: "5:30 PM – 6:30 PM" },
                  { icon: Calendar, label: "4 Sessions", desc: "May 13 through June 3" },
                  { icon: Utensils, label: "Dinner Included", desc: "Final night celebration" },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="bg-[#f8f7f4] rounded-xl p-4 border border-gray-100">
                    <Icon size={16} className="text-[#C9A96E] mb-2" />
                    <div className="text-sm font-semibold text-[#1B2A4A]">{label}</div>
                    <div className="text-xs text-gray-500">{desc}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#1B2A4A] text-white font-semibold rounded-md hover:bg-[#2a3d62] transition-colors"
                >
                  Register Now <ArrowRight size={18} />
                </Link>
                <a
                  href="tel:5196768772"
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#1B2A4A] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#1B2A4A] hover:text-white transition-colors"
                >
                  Call (519) 676-8772
                </a>
              </div>
            </div>

            {/* Right: Schedule */}
            <div>
              <div className="bg-[#f8f7f4] rounded-2xl p-8 border border-gray-100">
                <h3 className="text-xl font-serif font-bold text-[#1B2A4A] mb-6">2026 Schedule</h3>
                <div className="space-y-3">
                  {schedule.map((s) => (
                    <div
                      key={s.date}
                      className={`flex items-center justify-between rounded-xl px-5 py-4 ${
                        s.highlight
                          ? "bg-[#1B2A4A] text-white"
                          : s.label === "Rain Date"
                          ? "bg-white border border-dashed border-gray-200 text-gray-400"
                          : "bg-white border border-gray-100 text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar size={15} className={s.highlight ? "text-[#C9A96E]" : s.label === "Rain Date" ? "text-gray-300" : "text-[#C9A96E]"} />
                        <span className={`font-semibold text-sm ${s.highlight ? "text-white" : ""}`}>{s.date}</span>
                      </div>
                      <span className={`text-sm ${s.highlight ? "text-[#C9A96E] font-semibold" : s.label === "Rain Date" ? "italic text-xs" : ""}`}>
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-5 italic">
                  * Clinics run Wednesdays 5:30 – 6:30 PM. June 10th is the scheduled rain date.
                </p>
              </div>

              {/* What's included box */}
              <div className="bg-[#1B2A4A] rounded-2xl p-6 mt-6">
                <h3 className="font-serif font-bold text-white mb-4">What's Included</h3>
                <ul className="space-y-2">
                  {[
                    "4 one-hour group clinic sessions",
                    "Professional instruction from our team",
                    "Use of the practice facility",
                    "Dinner & drink on the final evening",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-white/80">
                      <Check size={14} className="text-[#C9A96E] mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">Ready to Start Playing?</h2>
          <p className="text-white/60 mb-8">Spots are limited — register early to secure your place in the 2026 Ladies Beginner Clinic.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors"
            >
              Register Now <ArrowRight size={18} />
            </Link>
            <a
              href="tel:5196768772"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors"
            >
              Call (519) 676-8772
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LadiesClinics;
